import { Modal, ModalSizeEnum, PrimaryButton, SecondaryButton, toast } from '@systemeio/ui'
import { ConfirmationEmailInterface } from 'modules/digital-product/types/confirmation-email-interface'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import React, { FC, useEffect, useState } from 'react'
import { useSetDefaultEmail } from 'shared/components/dashboard-mail-editor/hooks/use-set-default-email'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import {
  ConfirmationEmailEditor,
  FILE_LINKS_SUBSTITUTE,
  PRODUCT_NAME_SUBSTITUTE,
} from './confirmation-email-editor'

type Props = {
  confirmationEmail: DigitalProductChangeInterface['confirmationEmail']
  onChange: (data: DigitalProductChangeInterface['confirmationEmail']) => void
  errors: DigitalProductErrorInterface['confirmationEmail']
}

export const ConfirmationEmail: FC<Props> = ({ confirmationEmail, onChange, errors }) => {
  const { t } = useLocoTranslation()
  const { storeId } = useStoreQuery()

  const { data: storeView } = useStore(storeId)

  const [defaultConfirmationEmail, setDefaultConfirmationEmail] =
    useState<ConfirmationEmailInterface>({
      subject: t('dashboard.store.product.download.confirmation_email.default_subject'),
      body: t('dashboard.store.product.download.confirmation_email.default_body', {
        product_name: `{${PRODUCT_NAME_SUBSTITUTE}}`,
        downloadable_file_links: `{${FILE_LINKS_SUBSTITUTE}}`,
        lng: storeView?.locale,
      }),
      fromName: '',
      fromEmail: '',
      previewText: '',
    })

  useSetDefaultEmail(defaultConfirmationEmail, data => setDefaultConfirmationEmail(data))

  const [opened, setOpened] = useState(false)

  const [tempState, setTempState] = useState<ConfirmationEmailInterface>(
    confirmationEmail ?? defaultConfirmationEmail,
  )

  useEffect(() => {
    if (confirmationEmail) {
      setTempState(confirmationEmail)
    }
  }, [confirmationEmail])

  const canSave =
    !!tempState.fromEmail && !!tempState.fromName && !!tempState.body && !!tempState.subject

  const onSave = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    if (!tempState.body.includes(`{${FILE_LINKS_SUBSTITUTE}}`)) {
      toast.error(t('dashboard.store.product.download.confirmation_email.require_fields'))
    } else {
      onChange(tempState)
      setOpened(false)
    }
  }

  const hasConfirmationEmailError =
    !!errors.subject || !!errors.body || !!errors.fromName || !!errors.fromEmail

  return (
    <>
      <PrimaryButton
        className={twMerge('w-fit', hasConfirmationEmailError && 'bg-danger hover:bg-danger')}
        onClick={() => setOpened(true)}
      >
        {t('dashboard.store.product.download.confirmation_email.edit')}
      </PrimaryButton>
      <Modal
        opened={opened}
        size={ModalSizeEnum.big}
        onClose={() => setOpened(false)}
        title={t('dashboard.store.product.download.confirmation_email.title')}
        afterLeave={() => setTempState(confirmationEmail ?? defaultConfirmationEmail)}
      >
        <form className="flex flex-col gap-6 lg:gap-7">
          <ConfirmationEmailEditor
            tempState={tempState}
            onChange={setTempState}
            errors={{ ...errors, common: [] }}
          />
          <div className="flex justify-center gap-4">
            <SecondaryButton
              width="large"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                setOpened(false)
              }}
              type="submit"
            >
              {t('confirmation_modal.cancel')}
            </SecondaryButton>
            <PrimaryButton disabled={!canSave} width="large" onClick={onSave}>
              {t('global.save')}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  )
}
